import React, { useState, VFC } from 'react'
import styles from '../../styles/front/search_page/SearchForm.module.sass'

type SearchFormProps = {
  tags: Tags[]
  selected_tags: string[]
  search_target_category_tags: any[]
  selected_search_target_category_tags: string[]
  authors?: Author[]
  selected_author_ids?: string[]
  areas: Areas
  selected_prefectures: string[]
  selected_city_names: string[]
  type: string
  types: { key: string; value: string }[]
  theme_color: string
  eventTypeSettings: EventTypeSetting[]
  eventTypeCategoryId: number
  selected_type: string
  modelhouse_tag_name: string
  customEventTypeTags: string[]
  areaSlug?: string
}

type Tags = {
  category_name: string
  tags: any[]
}

type Areas = {
  prefecture_city: PrefectureCity[]
  city_list: any[]
}

type PrefectureCity = {
  prefecture: any
  area: any[]
}

type EventTypeSetting = {
  type_ja: string
  type: string
  custom_event_type_name: string
}

type Author = {
  id: number
  display_name: string
}

const SearchForm: VFC<SearchFormProps> = (props) => {
  const {
    tags,
    selected_tags,
    areas,
    search_target_category_tags,
    selected_search_target_category_tags,
    authors = [],
    selected_author_ids = [],
    selected_prefectures,
    selected_city_names,
    type,
    types,
    theme_color,
    selected_type,
    eventTypeSettings,
    eventTypeCategoryId,
    modelhouse_tag_name,
    customEventTypeTags = [],
    areaSlug = '',
  } = props

  const [isShowTagModal, setIsShowTagModal] = useState<boolean>(false)
  const [isShowSelectArea, setIsShowSelectArea] = useState<boolean>(false)
  const [isShowCategory, setIsShowCategory] = useState<boolean>(false)
  const [selectTags, setSelectTags] = useState<string[]>(selected_tags)
  const [selectAuthorIds, setSelectAuthorIds] = useState<string[]>(selected_author_ids.map(String))
  const [selectPrefectures, setSelectPrefectures] = useState<string[]>(selected_prefectures)
  const [selectCityNames, setSelectCityNames] = useState<string[]>(selected_city_names)

  let selectedSearchTargetCategoryTag = selected_search_target_category_tags
  let topSearchTargetCategoryTags = []
  let model_house_tag = search_target_category_tags.filter((x) => x.name == modelhouse_tag_name)[0]
  let not_model_house_tags = search_target_category_tags.filter((x) => x.name != modelhouse_tag_name)

  topSearchTargetCategoryTags =
    type === 'events' && model_house_tag !== undefined
      ? search_target_category_tags.filter((x) => x.name !== modelhouse_tag_name)
      : search_target_category_tags

  function setTag(name: string) {
    if (selectTags.includes(name)) {
      setSelectTags(selectTags.filter((tag) => tag !== name))
      return
    }
    setSelectTags([...selectTags, name])
  }

  function setAuthor(id: number) {
    const authorId = String(id)
    if (selectAuthorIds.includes(authorId)) {
      setSelectAuthorIds(selectAuthorIds.filter((selectedAuthorId) => selectedAuthorId !== authorId))
      return
    }
    setSelectAuthorIds([...selectAuthorIds, authorId])
  }

  function setArea(is_prefecture: boolean, name: string) {
    if (is_prefecture) {
      if (selectPrefectures.includes(name)) {
        setSelectPrefectures(selectPrefectures.filter((area) => area !== name))
        return
      }
      setSelectPrefectures([...selectPrefectures, name])
      return
    }

    if (selectCityNames.includes(name)) {
      setSelectCityNames((selectCityNames) => selectCityNames.filter((area) => area !== name))
      return
    }

    setSelectCityNames((selectCityNames) => [...selectCityNames, name])
  }

  function setDesignationArea(target, area) {
    for (let i = 0; i < area.cities.length; i++) {
      setSelectCityNames((selectCityNames) => selectCityNames.filter((a) => a !== area.cities[i].city_name))

      if (target.checked) {
        setSelectCityNames((selectCityNames) => [...selectCityNames, area.cities[i].city_name])
      }
    }
  }

  function clickSelectTargetTag(name: string) {
    // すべてを押下した時の処理
    if (name === 'all') {
      let query = ''
      if (selected_type && type === 'columns') query = `super_category=${selected_type}`
      query = appendAuthorIds(query)
      location.href = `${location.origin}/${type}${query ? '?' + query : ''}`
      return
    }

    // すべてが活性化している状態で違うタグを押下した時
    if (isSelectAll()) {
      selectedSearchTargetCategoryTag = [name]
      searchSearchCategoryTag()
      return
    }

    selectedSearchTargetCategoryTag = [name]
    searchSearchCategoryTag()
  }

  function encode(value: string) {
    // タグのR+houseなどの+がURlで使えないためエンコードする
    return encodeURIComponent(value)
  }

  function appendQueryParam(query: string, param: string) {
    if (!param) return query
    return query.length > 0 ? `${query}&${param}` : param
  }

  function appendAuthorIds(query: string) {
    if (selectAuthorIds.length === 0) return query
    return appendQueryParam(query, `author_ids=${selectAuthorIds.join('*')}`)
  }

  function searchTag() {
    let query = ''
    if (selectTags.length > 0) query = `tags=${encode(selectTags.join('*'))}`
    query = appendAuthorIds(query)
    if (selected_prefectures.length > 0) query = `${query}&prefectures=${selected_prefectures.join('*')}`
    if (selectedSearchTargetCategoryTag.length > 0)
      query = `${query}&search_target_tags=${encode(selectedSearchTargetCategoryTag.join('*'))}`
    if (selected_type && type === 'events') query = `${query}&event_type=${selected_type}`
    if (selected_type && type === 'columns') query = `${query}&super_category=${selected_type}`

    if (selectCityNames.length > 0) {
      const slug = findSlugByName(selectCityNames[0]) || areaSlug
      const publishedAreaPage = findStatusByName(selectCityNames[0])

      if (slug?.length > 0 && selectCityNames.length === 1 && publishedAreaPage) {
        const queryParams = query.length > 0 ? `?${formatQueryParam(query)}` : ''
        location.href = `${location.origin}/${slug}/${type}${queryParams}`
        return
      } else {
        query = `${query}&city_names=${selectCityNames.join('*')}`
      }
    }

    location.href = `${location.origin}/${type}?${formatQueryParam(query)}`
  }

  const findSlugByName = (name) => {
    const areaItem = areas.map((area) => area.area).flat()
    const cities = areaItem.map((area) => area.cities).flat()
    return cities.filter((city) => city.city_name === name)[0]?.slug
  }

  const findStatusByName = (name) => {
    {
      const areaItem = areas.map((area) => area.area).flat()
      const cities = areaItem.map((area) => area.cities).flat()
      return cities.filter((city) => city.city_name === name)[0]?.published_area_page
    }
  }

  function searchArea() {
    let query = ''
    if (selectPrefectures.length > 0) query = `prefectures=${selectPrefectures.join('*')}`
    if (selected_tags.length > 0) query = `${query}&tags=${encode(selected_tags.join('*'))}`
    query = appendAuthorIds(query)
    if (selectedSearchTargetCategoryTag.length > 0)
      query = `${query}&search_target_tags=${encode(selectedSearchTargetCategoryTag.join('*'))}`
    if (selected_type && type === 'events') query = `${query}&event_type=${selected_type}`
    if (selected_type && type === 'columns') query = `${query}&super_category=${selected_type}`

    if (selectCityNames.length > 0) {
      const slug = findSlugByName(selectCityNames[0]) || areaSlug
      const publishedAreaPage = findStatusByName(selectCityNames[0])

      if (slug?.length > 0 && selectCityNames.length === 1 && publishedAreaPage) {
        const queryParams = query.length > 0 ? `?${formatQueryParam(query)}` : ''
        location.href = `${location.origin}/${slug}/${type}${queryParams}`
        return
      } else {
        query = `${query}&city_names=${selectCityNames.join('*')}`
      }
    }

    location.href = `${location.origin}/${type}?${formatQueryParam(query)}`
  }

  function searchSearchCategoryTag() {
    let query = ''
    if (selectedSearchTargetCategoryTag.length > 0)
      query = `search_target_tags=${encode(selectedSearchTargetCategoryTag.join('*'))}`
    if (selected_prefectures.length > 0) query = `${query}&prefectures=${selected_prefectures.join('*')}`
    if (selected_city_names.length > 0) {
      const slug = findSlugByName(selectCityNames[0]) || areaSlug
      const publishedAreaPage = findStatusByName(selectCityNames[0]) || areaSlug?.length > 0

      if (slug?.length > 0 && selectCityNames.length === 1 && publishedAreaPage) {
        if (selected_type && type === 'events') query = `${query}&event_type=${selected_type}`
        if (selected_type && type === 'columns') query = `${query}&super_category=${selected_type}`
        query = appendAuthorIds(query)
        const queryParams = query.length > 0 ? `?${formatQueryParam(query)}` : ''

        location.href = `${location.origin}/${slug}/${type}${queryParams}`
        return
      } else {
        query = `${query}&city_names=${selected_city_names.join('*')}`
      }
    }
    if (selected_tags.length > 0) query = `${query}&tags=${encode(selected_tags.join('*'))}`
    query = appendAuthorIds(query)
    if (selected_type && type === 'events') query = `${query}&event_type=${selected_type}`
    if (selected_type && type === 'columns') query = `${query}&super_category=${selected_type}`
    location.href = `${location.origin}/${type}?${formatQueryParam(query)}`
  }

  function clickType(value: string) {
    let query = ''
    if (selectedSearchTargetCategoryTag.length > 0)
      query = `search_target_tags=${encode(selectedSearchTargetCategoryTag.join('*'))}`
    if (selected_prefectures.length > 0) query = `${query}&prefectures=${selected_prefectures.join('*')}`
    if (selected_city_names.length > 0) {
      const slug = findSlugByName(selectCityNames[0]) || areaSlug
      const publishedAreaPage = findStatusByName(selectCityNames[0]) || areaSlug?.length > 0

      if (slug?.length > 0 && selectCityNames.length === 1 && publishedAreaPage) {
        if (type === 'events') query = `${query}&event_type=${selected_type}`
        if (type === 'columns') query = `super_category=${value}`
        query = appendAuthorIds(query)
        const queryParams = query.length > 0 ? `?${formatQueryParam(query)}` : ''

        location.href = `${location.origin}/${slug}/${type}${queryParams}`
        return
      } else {
        query = `${query}&city_names=${selected_city_names.join('*')}`
      }
    }
    if (selected_tags.length > 0) query = `${query}&tags=${encode(selected_tags.join('*'))}`
    if (type === 'events') query = `${query}&event_type=${selected_type}`
    if (type === 'columns') query = `super_category=${value}`
    query = appendAuthorIds(query)
    location.href = `${location.origin}/${type}?${formatQueryParam(query)}`
  }

  const clickModelHouseTag = () => {
    if (selected_city_names.length > 0) {
      const slug = findSlugByName(selectCityNames[0]) || areaSlug
      const publishedAreaPage = findStatusByName(selectCityNames[0]) || areaSlug?.length > 0
      if (slug?.length > 0 && selectCityNames.length === 1 && publishedAreaPage) {
        location.href = `${location.origin}/${slug}/modelhouse`
        return
      }
    }

    location.href = `${location.origin}/modelhouse`
  }

  function isSelectAll() {
    return selectedSearchTargetCategoryTag.length === search_target_category_tags.length
  }

  function resetArea() {
    setSelectCityNames([])
    setSelectPrefectures([])

    const designationCityCheckboxs = document.querySelectorAll(
      "input[name^='designationCity_']"
    ) as NodeListOf<HTMLInputElement>
    designationCityCheckboxs.forEach((checkbox) => (checkbox.checked = false))
  }

  function resetFilter() {
    setSelectTags([])
    setSelectAuthorIds([])
  }

  function clickCategoryBtn() {
    if (!isShowCategory) setIsShowCategory(true)
  }

  function openTagModal() {
    setIsShowTagModal(true)
    document.body.setAttribute('style', 'overflow: hidden;')
  }

  function closeTagModal() {
    setIsShowTagModal(false)
    document.body.setAttribute('style', 'overflow: visible;')
  }

  function openAreaModal() {
    setIsShowSelectArea(true)
    if (window.innerWidth < 989) document.body.setAttribute('style', 'overflow: hidden;')
  }

  function closeAreaModal() {
    setIsShowSelectArea(false)
    document.body.setAttribute('style', 'overflow: visible;')
  }

  const closeWithClickOutSideMethod = (e, setter) => {
    if (e.target === e.currentTarget) {
      //メニューの外側をクリックしたときだけメニューを閉じる
      setter(false)
      document.body.setAttribute('style', 'overflow: visible;')
    }
  }

  function formatQueryParam(query) {
    return query.startsWith('&') ? query.substring(1) : query
  }

  const outputTagName = (tag: any) => {
    if (
      eventTypeSettings.length > 0 &&
      tag.tag_category_id === eventTypeCategoryId &&
      customEventTypeTags.includes(tag.name)
    ) {
      const targeEventTypeSetting = eventTypeSettings.find((eventTypeSetting) => eventTypeSetting.type_ja == tag.name)
      return targeEventTypeSetting ? targeEventTypeSetting.custom_event_type_name : tag.name
    } else {
      return tag.name
    }
  }

  return (
    <>
      {types && (
        <div className={styles.types}>
          {types.map((type) => (
            <div
              key={type.key}
              className={`${styles.searchWordBtn} ${type.key == selected_type
                ? styles[theme_color]
                : styles.normal + ' ' + styles[theme_color] + ' ' + styles['-primary-light']
                }`}
              onClick={() => clickType(type.key)}
            >
              {type.value}
            </div>
          ))}
        </div>
      )}
      {(search_target_category_tags.length > 1 || areas.length > 0 || tags.length > 0 || authors.length > 0) && (
        <div>
          <div className={styles.searchForm + ' ' + styles[theme_color] + ' ' + styles['-secondary-light']}>
            <div className={styles.searchFormContainer}>
              {/* pcで表示 */}
              {type !== 'modelhouse' && search_target_category_tags.length > 1 && (
                <div
                  className={`${styles.searchWordsTop} ${search_target_category_tags.length < 5 ? styles.center : ''}`}
                >
                  {search_target_category_tags.length > 1 && (
                    <div
                      key={'all'}
                      className={`${styles.searchWordBtn} ${isSelectAll() || selectedSearchTargetCategoryTag.length === 0
                        ? styles[theme_color]
                        : styles.normal + ' ' + styles[theme_color] + ' ' + styles['-primary-light']
                        }`}
                      onClick={() => clickSelectTargetTag('all')}
                    >
                      すべて
                    </div>
                  )}
                  {model_house_tag !== undefined && (
                    <div
                      key={model_house_tag.id}
                      className={`${styles.searchWordBtn} ${selectedSearchTargetCategoryTag.includes(model_house_tag.name) && true
                        ? styles[theme_color]
                        : styles.normal + ' ' + styles[theme_color] + ' ' + styles['-primary-light']
                        }`}
                      onClick={() => clickModelHouseTag()}
                    >
                      {model_house_tag.name}
                    </div>
                  )}
                  {topSearchTargetCategoryTags.map((tag) => (
                    <div
                      key={tag.id}
                      className={`${styles.searchWordBtn} ${selectedSearchTargetCategoryTag.includes(outputTagName(tag)) && !isSelectAll()
                        ? styles[theme_color]
                        : styles.normal + ' ' + styles[theme_color] + ' ' + styles['-primary-light']
                        }`}
                      onClick={() => clickSelectTargetTag(outputTagName(tag))}
                    >
                      {outputTagName(tag)}
                    </div>
                  ))}
                </div>
              )}
              {/* spで表示 */}
              {type !== 'modelhouse' && search_target_category_tags.length > 1 && (
                <div className={styles.categoryBtn + ' ' + styles[theme_color]} onClick={clickCategoryBtn}>
                  カテゴリを選択する
                  {!isShowCategory && (
                    <div className={styles.categoryLineArrow}>
                      <div className={styles.line}></div>
                      <div className={styles.arrow}></div>
                    </div>
                  )}
                  {isShowCategory && (
                    <div className={`${styles.close} ${styles.categoryClose}`} data-icon onClick={() => setIsShowCategory(false)}>
                      clear
                    </div>
                  )}
                  {isShowCategory && (
                    <div className={styles.spCategoryList}>
                      {search_target_category_tags.length > 0 && (
                        <div key={'all'} className={styles.spCategory} onClick={() => clickSelectTargetTag('all')}>
                          <div className={styles.spCategoryText}>すべて</div>
                          <div className={styles.lineArrow}>
                            <div className={styles.line}></div>
                            <div className={styles.arrow}></div>
                          </div>
                        </div>
                      )}
                      {model_house_tag !== undefined && (
                        <div
                          key={model_house_tag.id}
                          className={styles.spCategory}
                          onClick={() => clickModelHouseTag()}
                        >
                          <div className={styles.spCategoryText}>{model_house_tag.name}</div>
                          <div className={styles.lineArrow}>
                            <div className={styles.line}></div>
                            <div className={styles.arrow}></div>
                          </div>
                        </div>
                      )}
                      {not_model_house_tags.map((tag) => (
                        <div
                          key={tag.id}
                          className={styles.spCategory}
                          onClick={() => clickSelectTargetTag(outputTagName(tag))}
                        >
                          <div className={styles.spCategoryText}>{outputTagName(tag)}</div>
                          <div className={styles.lineArrow}>
                            <div className={styles.line}></div>
                            <div className={styles.arrow}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {(tags.length > 0 || areas.length > 0 || authors.length > 0) && (
                <div
                  className={`${styles.tagsAndAreasBtns} ${type === 'events' && search_target_category_tags.length == 0 ? styles.event : ''
                    }`}
                >
                  {(tags.length > 0 || authors.length > 0) && (
                    <div className={styles.showModalBtn + ' ' + styles[theme_color]} onClick={openTagModal}>
                      <div className={styles.showModalBtnText}>検索条件を絞り込む</div>
                      <div className={styles.downArrow + ' ' + styles[theme_color]}></div>
                    </div>
                  )}
                  {areas.length > 0 && (
                    <div
                      className={styles.showModalBtn + ' ' + styles.area + ' ' + styles[theme_color]}
                      onClick={openAreaModal}
                    >
                      <div className={styles.showModalBtnText}>市区町村で絞り込む</div>
                      <div className={styles.downArrow + ' ' + styles[theme_color]}></div>
                    </div>
                  )}
                  {isShowSelectArea && (
                    <div
                      className={styles.areaModalBackground}
                      onClick={(e) => {
                        closeWithClickOutSideMethod(e, setIsShowSelectArea)
                      }}
                    >
                      <div className={styles.areaModal}>
                        <div className={styles.areaHeader}>
                          <div className={styles.areaHeaderText}>市区町村を絞り込む</div>
                          <div className={styles.arrowArea} onClick={closeAreaModal}>
                            <div className={styles.areaClose + ' ' + styles[theme_color]}></div>
                          </div>
                          <div className={`${styles.close} ${styles.areaCloseIcon}`} data-icon onClick={closeAreaModal}>
                            clear
                          </div>
                        </div>
                        <div className={styles.areaBody}>
                          {areas.map((prefecture, prefectureIndex) => (
                            <div key={prefectureIndex} className={styles.prefecture}>
                              <span className={styles.prefectureLabel}>{prefecture.prefecture.prefecture_name}</span>
                              {prefecture.is_exist_prefecture && (
                                <div key={`prefecture_${prefectureIndex}`} className={styles.checkbox}>
                                  <input
                                    id={`prefecture_${prefectureIndex}`}
                                    name={`prefecture_${prefectureIndex}`}
                                    type={'checkbox'}
                                    value={prefecture.prefecture.prefecture_name}
                                    onChange={() => setArea(true, prefecture.prefecture.prefecture_name)}
                                    checked={selectPrefectures.includes(prefecture.prefecture.prefecture_name)}
                                  />
                                  <label
                                    htmlFor={`prefecture_${prefectureIndex}`}
                                    className={`${styles.areaLabel} ${styles.topLabel}`}
                                  >
                                    {prefecture.prefecture.prefecture_name}
                                  </label>
                                </div>
                              )}
                              <div>
                                {prefecture.area.map((area, designationIndex) => (
                                  <div key={`${prefectureIndex}_${designationIndex}`} className={styles.cityTopLabel}>
                                    {area.designation_city && (
                                      <div
                                        key={`designationCity_${prefectureIndex}_${designationIndex}`}
                                        className={`${styles.designation_city} ${styles.checkbox}`}
                                      >
                                        <input
                                          id={`designationCity_${prefectureIndex}_${designationIndex}`}
                                          name={`designationCity_${prefectureIndex}_${designationIndex}`}
                                          type={'checkbox'}
                                          value={area.designation_city}
                                          onChange={(e) => setDesignationArea(e.target, area)}
                                        />
                                        <label
                                          htmlFor={`designationCity_${prefectureIndex}_${designationIndex}`}
                                          className={styles.areaLabel}
                                        >
                                          {area.designation_city}
                                        </label>
                                      </div>
                                    )}
                                    <div
                                      className={`${styles.cityNameList} ${area.designation_city ? styles.designationCityList : ''
                                        }`}
                                    >
                                      {area.cities.map((city) => (
                                        <div
                                          key={`city_${city.id}`}
                                          className={`${styles.cityName} ${styles.checkbox}`}
                                        >
                                          <input
                                            id={`city_${city.id}`}
                                            name={`city_${city.id}`}
                                            type={'checkbox'}
                                            value={city.city_name}
                                            onChange={() => setArea(false, city.city_name)}
                                            checked={selectCityNames.includes(city.city_name)}
                                          />
                                          <label htmlFor={`city_${city.id}`} className={styles.areaLabel}>
                                            {area.designation_city ? (
                                              <>{city.city_name.replace(area.designation_city, '')}</>
                                            ) : (
                                              <>{city.city_name}</>
                                            )}
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className={styles.areaFooter}>
                          <div className={styles.areaFooterContainer}>
                            <div className={styles.resetBtn + ' ' + styles[theme_color]} onClick={resetArea}>
                              リセット
                            </div>
                            <div className={styles.searchBtn + ' ' + styles[theme_color]} onClick={searchArea}>
                              検索
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {isShowTagModal && (
            <div
              className={styles.modalBackground}
              onClick={(e) => {
                closeWithClickOutSideMethod(e, setIsShowTagModal)
              }}
            >
              <div className={styles.modal}>
                <div className={styles.modalHeader}>
                  <div>絞り込み条件</div>
                  <div className={styles.close} data-icon onClick={closeTagModal}>
                    clear
                  </div>
                </div>
                <div className={styles.tagList}>
                  {tags.map((tag_category, index) => (
                    <div key={index}>
                      {tag_category.tags.length > 0 && (
                        <div key={index} className={styles.categoryTags + ' ' + styles[theme_color]}>
                          <div
                            className={
                              styles.categoryNameContainer +
                              ' ' +
                              styles[theme_color] +
                              ' ' +
                              styles['-secondary-light']
                            }
                          >
                            <div className={styles.categoryName}>{tag_category.category_name}</div>
                          </div>
                          <div className={styles.tags}>
                            {tag_category.tags.map((tag) => (
                              <div key={tag.id} className={`${styles.checkbox} ${styles.tagCheckbox}`}>
                                <input
                                  id={`tag_${tag.id}`}
                                  name={`tag_${tag.id}`}
                                  type={'checkbox'}
                                  value={outputTagName(tag)}
                                  onChange={() => setTag(outputTagName(tag))}
                                  checked={selectTags.includes(outputTagName(tag))}
                                />
                                <label htmlFor={`tag_${tag.id}`} className={styles.tagLabel}>
                                  {outputTagName(tag)}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {authors.length > 0 && (
                    <div>
                      <div className={styles.categoryTags + ' ' + styles[theme_color]}>
                        <div
                          className={
                            styles.categoryNameContainer +
                            ' ' +
                            styles[theme_color] +
                            ' ' +
                            styles['-secondary-light']
                          }
                        >
                          <div className={styles.categoryName}>著者</div>
                        </div>
                        <div className={styles.tags}>
                          {authors.map((author) => (
                            <div key={author.id} className={`${styles.checkbox} ${styles.tagCheckbox}`}>
                              <input
                                id={`author_${author.id}`}
                                name={`author_${author.id}`}
                                type={'checkbox'}
                                value={String(author.id)}
                                onChange={() => setAuthor(author.id)}
                                checked={selectAuthorIds.includes(String(author.id))}
                              />
                              <label htmlFor={`author_${author.id}`} className={styles.tagLabel}>
                                {author.display_name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.modalFooter}>
                  <div className={styles.modalFooterContainer}>
                    <div className={styles.resetBtn + ' ' + styles[theme_color]} onClick={resetFilter}>
                      リセット
                    </div>
                    <div className={styles.searchBtn + ' ' + styles[theme_color]} onClick={searchTag}>
                      検索
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default SearchForm
